import { Permission } from '@act/shared/authorization';
import {
  NeedDto,
  NeedsActionDto,
  NeedsActivityDto,
} from '@act/shared/data-transfer-objects';
import type {
  AddNeedResourceDetailsToActionRequest,
  NeedAddActionRequest,
} from '@act/shared/types';
import { FormBuilder } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Dialog, DialogContent, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TrackingProvider } from 'react-components/tracking';
import Loader from '@act/react-components/workflows/Loader';
import { useFieldArray, useForm } from 'react-hook-form';
import { needRequests } from 'redux/requests/need.requests';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice.slice';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hook';
import { getMemberNeeds } from 'redux/slices/needs.slice';
import { useAppDispatch } from 'redux/storeHooks';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { themeColors } from 'toolkit/styles/colors';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import { useToast } from '../app/ReusableToast';
import { useAddActionFormConfig } from './AddActionDialogFormConfig';
import CloseableDialogTitle from './components/dialog-components/CloseableDialogTitle';
import SubmittableDialogActions from './components/dialog-components/SubmittableDialogActions';
import {
  ActionFormValues,
  NeedActionFormSchema,
} from './components/useNeedActionFormtypes';
import useConfirmationAlertDialog from './hooks/useConfirmationAlertDialog.hook';
import useRichMessageDialog from './hooks/useRichMessageDialog.hook';
import useUnsavedChangesDialog from './hooks/useUnsavedChangesDialog.hook';

type ResourceDetailsRequestWithId = AddNeedResourceDetailsToActionRequest & {
  id: number;
};

export interface AddActionDialogUniqueProps {
  need: NeedDto;
  action?: NeedsActionDto;
}

export type AddActionCallbackInput = null;
export type AddActionDialogProps = GenericDialogProps<null> &
  AddActionDialogUniqueProps;

export const AddActionDialog = ({
  need,
  action,
  open,
  setOpen,
}: AddActionDialogProps) => {
  const dispatch = useAppDispatch();
  const canDeleteNeedActions = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_DELETE_NEED_ACTIONS
  );
  const [showError, setShowError] = useState<boolean>(false);
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const openLearnMoreDialog = useRichMessageDialog();
  const openAbandonResourceDialog = useConfirmationAlertDialog();
  const openConfirmDeleteActionDialog = useConfirmationAlertDialog();
  const [deletedAction, isDeletingAction, deleteAction] = useApiCall(
    needRequests.deleteAction
  );
  const researchActivity =
    metaSliceSelectors.useSelectResourceResearchActivity();
  const isResourcingResearch =
    action?.activityId && action.activityId === researchActivity?.id;

  const overrideValues: Partial<ActionFormValues> = {};
  if (isResourcingResearch && researchActivity && researchActivity.id) {
    overrideValues.activityId = researchActivity.id;
    overrideValues.markAsFulfilled = false;
  }

  const referralsCountRef = useRef<number>(null);
  const [selectedActivity, setSelectedActivity] =
    useState<NeedsActivityDto>(null);
  const [expandedForm, setExpandedForm] = useState<string>(null);
  const [durationFocused, setDurationFocused] = useState(false);

  const minDate = DateTime.now().minus({ week: 1 });

  const { activities, issues } =
    metaSliceSelectors.useSelectNeedsMetadata().data;

  const issue = useMemo(
    () => issues.find(i => i.id === need?.issueId),
    [need?.issueId, issues]
  );

  const activityOptions: NeedsActivityDto[] = useMemo(
    () =>
      activities
        .filter(a => {
          let latestAction: NeedsActionDto;
          if (action) {
            const allActions = [action, ...(action.subsequentActions || [])];
            allActions.sort((a, b) => b.createdAt - a.createdAt);
            latestAction = allActions[0];
          }
          return (
            issue.activityIds.includes(a.id) ||
            latestAction?.activityId === a.id
          );
        })
        .sort((a, b) => {
          if (b.label < a.label) return 1;
          if (b.label > a.label) return -1;
          return 0;
        })
        .map(a => {
          return a;
        }),
    [action, activities, issue]
  );

  const defaultValues: Partial<ActionFormValues> = {
    activityId: overrideValues.activityId,
    date: undefined,
    duration: '',
    description: undefined,
    resourceReferred: false,
    resourceDetails: [],
    markAsFulfilled: overrideValues.markAsFulfilled,
  };

  const { control, handleSubmit, reset, setValue, watch, formState } = useForm({
    defaultValues,
    criteriaMode: 'all',
    mode: 'onBlur',
    resolver: zodResolver(NeedActionFormSchema),
  });

  const {
    append,
    remove,
    fields: resourceDetails,
  } = useFieldArray({
    name: 'resourceDetails',
    control,
    keyName: 'key',
  });

  const formValues = watch();
  const referrals = watch('resourceDetails');

  // Initialize form with action data if editing
  useEffect(() => {
    if (action) {
      const allActions = [action, ...(action.subsequentActions || [])];
      allActions.sort((a, b) => b.createdAt - a.createdAt);
      const latestAction = allActions[0];

      if (
        latestAction?.resourceReferrals &&
        Array.isArray(latestAction.resourceReferrals)
      ) {
        [...(latestAction?.resourceReferrals || [])]
          ?.sort((a, b) => a.createdAt - b.createdAt)
          .forEach(referral => {
            referralsCountRef.current = referralsCountRef.current + 1;
            append({
              id: referral?.id,
              resourceName: referral?.resourceName,
              contactName: referral?.contactName,
              phoneNumber: referral?.phoneNumber,
              url: referral?.url,
              streetAddress: referral?.streetAddress,
              city: referral?.city,
              state: referral?.state,
              zipcode: referral?.zipcode,
              shouldDelete: false,
            });
          });
      }

      reset({
        activityId: latestAction?.activityId,
        date: `${latestAction?.date}`,
        duration: latestAction?.durationInMinutes?.toString() ?? '',
        description: latestAction.description,
        resourceReferred: referralsCountRef?.current > 0,
        ...overrideValues,
      });
    }
  }, [action, append, reset]);

  useEffect(() => {
    function findActivityById(): NeedsActivityDto {
      if (!formValues?.activityId) {
        return null;
      }

      return activities.find(a => a.id === formValues.activityId);
    }

    if (formValues.activityId) {
      setSelectedActivity(findActivityById());
    }
  }, [formValues?.activityId, activities]);

  useEffect(() => {
    if (referralsCountRef.current > resourceDetails.length) {
      referralsCountRef.current = referralsCountRef.current - 1;
    }
  }, [resourceDetails.length]);

  useEffect(() => {
    if (resourceDetails.length >= referralsCountRef.current) {
      referralsCountRef.current = resourceDetails.length;
      const key = resourceDetails[resourceDetails.length - 1]?.key;
      setExpandedForm(key);
    }
  }, [resourceDetails]);

  function getTimeWindowMessage(): string {
    if (!selectedActivity) {
      return 'Select an activity';
    }

    return `The target time window for this action is ~${selectedActivity?.defaultTimeFrameInMinutes} minutes.`;
  }

  function expandResourceForm(key: string) {
    if (expandedForm === key) {
      setExpandedForm(null);
    } else {
      setExpandedForm(key);
    }
  }

  function appendResourceDetails() {
    append({
      id: -1,
      resourceName: '',
      contactName: '',
      phoneNumber: undefined,
      url: '',
      streetAddress: '',
      city: '',
      state: '',
      zipcode: '',
      shouldDelete: false,
    });
  }

  function formatResourceReferrals(
    resourceRequests: ResourceDetailsRequestWithId[]
  ): AddNeedResourceDetailsToActionRequest[] {
    return resourceRequests.map(details => {
      const { id, phoneNumber, ...data } = details;
      const formattedPhoneNumber = phoneNumber
        ? '+1'.concat(phoneNumber?.replace(/\D/g, ''))
        : undefined;
      return {
        ...data,
        ...(formattedPhoneNumber && { phoneNumber: formattedPhoneNumber }),
        ...(id > 0 && { id: details.id }),
      };
    });
  }

  const confirmDeleteResource = async (index: number) => {
    const confirm = await openAbandonResourceDialog({
      title: 'Confirm delete',
      icon: <ErrorOutlineIcon />,
      iconColor: 'error',
      iconBorderColor: 'errorContainer',
      avatarCaption: 'Are you sure?',
      message: 'Are you sure you want to delete this resource?',
      confirmationText: 'Yes, delete now',
      confirmButtonColor: 'error',
    });
    if (confirm) {
      remove(index);
      if (formValues.resourceReferred && referrals.length === 1) {
        setValue('resourceReferred', false);
      }
    }
  };

  const [createdAction, isProcessing, addAction] = useApiCall(
    needRequests.addActionToNeed
  );

  const confirmDeleteAction = async () => {
    const confirm = await openConfirmDeleteActionDialog({
      title: 'Confirm delete',
      icon: <ErrorOutlineIcon />,
      iconColor: 'error',
      iconBorderColor: 'errorContainer',
      avatarCaption: 'Are you sure?',
      message: 'Are you sure you want to delete this action?',
    });
    if (confirm) {
      deleteAction({ params: { needId: need?.id, actionId: action?.id } });
    }
  };

  // Handle successful submission
  useEffect(() => {
    if (createdAction && !isProcessing) {
      toast('Action created successfully');
      reset();
      setShowError(false);
      setOpen(false);
    }
  }, [createdAction, isProcessing, toast, reset, setOpen]);

  useEffect(() => {
    if (deletedAction && !isDeletingAction) {
      toast('Action deleted successfully');
      if (need?.memberId) {
        dispatch(getMemberNeeds({ params: { memberId: need.memberId } }));
      }
      reset();
      setShowError(false);
      setOpen(false);
    }
  }, [
    deletedAction,
    isDeletingAction,
    toast,
    reset,
    setOpen,
    dispatch,
    need?.memberId,
  ]);

  // Prepare submission data
  const prepareSubmissionData = (formData: ActionFormValues) => {
    const finalData = { ...formData, ...overrideValues };
    const resources =
      finalData.resourceDetails?.length > 0
        ? formatResourceReferrals(
            finalData.resourceDetails as ResourceDetailsRequestWithId[]
          )
        : null;
    const body: NeedAddActionRequest = {
      activityId: finalData.activityId,
      date: finalData.date,
      durationInMinutes: Number(finalData.duration),
      description: finalData.description,
      ...(!!action && {
        originalActionId: action?.originalActionId
          ? action.originalActionId
          : action.id,
      }),
      markAsFulfilled: finalData.markAsFulfilled,
      ...(resources && {
        resourceReferrals: resources,
      }),
    };
    const params = { needId: need?.id };

    return { body, params };
  };

  // Handle form submission
  const handleSubmission = handleSubmit((formData: ActionFormValues) => {
    setShowError(false);
    const { body, params } = prepareSubmissionData(formData);
    addAction({ body, params });
  });

  const { isDirty } = formState;

  const handleClose = useCallback(
    (_event?: unknown, _reason?: string) => {
      // Prevent closing during submission
      if (isProcessing) {
        return;
      }

      // Check for unsaved changes and show confirmation dialog if needed
      if (isDirty) {
        void handleUnsavedChanges(() => {
          reset();
          setShowError(false);
          setOpen(false);
        });
      } else {
        reset();
        setShowError(false);
        setOpen(false);
      }
    },
    [handleUnsavedChanges, isDirty, reset, setOpen, isProcessing]
  );

  // Form configuration using FormBuilder
  const formConfig = useAddActionFormConfig({
    overrideValues,
    issue,
    activityOptions,
    selectedActivity,
    setValue,
    getTimeWindowMessage,
    openLearnMoreDialog,
    minDate,
    control,
    watch,
    durationFocused,
    setDurationFocused,
    formValues,
    resourceDetails,
    formState,
    expandedForm,
    expandResourceForm,
    confirmDeleteResource,
    appendResourceDetails,
    referralsCountRef,
    need,
  });

  const title = `${action ? 'Edit' : 'Add'} ${
    isResourcingResearch ? 'resource research' : 'action'
  }`;
  const subtitle = isResourcingResearch
    ? 'Record the resourcing activity'
    : 'Record the time and action performed';

  return (
    <TrackingProvider contextName="add-action-dialog">
      <Dialog open={open} onClose={handleClose} maxWidth="sm">
        <CloseableDialogTitle title={title} handleClose={handleClose} />
        <DialogContent sx={{ padding: '12px 24px 24px' }}>
          {showError && (
            <Typography variant="labelMedium" sx={{ color: themeColors.error }}>
              An error has occurred.
            </Typography>
          )}
          <Box sx={{ padding: '16px 0' }}>
            <Typography variant="titleMedium">{subtitle}</Typography>
          </Box>
          <hr
            style={{
              border: '1px solid #DCE4E8',
            }}
          />
          {isProcessing ? (
            <Loader />
          ) : (
            <FormBuilder
              config={formConfig}
              control={control}
              formState={formState}
              disabled={isProcessing}
            />
          )}
        </DialogContent>
        <SubmittableDialogActions
          submissionText="Save and close"
          isValid={formState.isValid}
          ignoreIsValid={true}
          disabled={isProcessing}
          handleClose={handleClose}
          handleSubmission={handleSubmission}
          handleDelete={
            (action && action?.canDelete) || canDeleteNeedActions
              ? confirmDeleteAction
              : null
          }
        />
      </Dialog>
    </TrackingProvider>
  );
};

export default AddActionDialog;
